import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { Observable } from 'rxjs/Rx';
import { MasonryComponent } from './masonry.component';

@Component({
  selector: 'nns-folder',
  styles: [`
    :host {
      position:relative;
      min-width:320px;
      padding:0 0 16px 0;
      z-index: 1;
    }
    .nns-wrapper {
      max-width:986px;
      min-width:320px;
      margin:0 auto;
      padding:0 10px;
      position:relative;
      box-sizing:border-box;
      width: 100%;
    }
    .nns-folder-title{
      font-weight: bold;
      font-size: 21px;
      padding: 0 10px;
      margin-bottom: 20px;
    }

    nns-item-folder-list:last-of-type /deep/ .nns-item-folder-link,
    nns-item-note-list:last-of-type /deep .nns-item-note-link {
      border-bottom:none;
    }

    nns-item-folder-list:first-of-type /deep/ .nns-item-folder-layer,
    nns-item-note-list:first-of-type /deep/ .nns-item-note-layer  {
      border-radius:2px 2px 0 0;
    }
    nns-item-folder-list:last-of-type /deep/ .nns-item-folder-layer,
    nns-item-note-list:last-of-type /deep/.nns-item-note-layer {
      border-radius:0 0 2px 2px;
    }
    .nns-folder-items.nns-folder-plates ::ng-deep .nns-item-note-link{
      border: 1px solid #EAEFF1;
      border-radius: 4px;
    }

    .nns-folder-items.nns-folder-plates.prepared {
      opacity: 0;
    }
    .nns-folder-folders-list {
      border-radius:2px;
      box-shadow:0px 1px 3px 0px rgba(0,0,0,0.20);
      margin:0 8px 16px 8px;
    }

    @media only screen and (max-width: 768px) {
      :host {
        padding:16px 0;
      }
    }

    .masonry-hide {
      visibility:hidden;
    }
  `, `
    :host-context(.nns-embed) {
      padding-bottom: 70px;
    }
    :host-context(.nns-theme-night) .nns-folder-items.nns-folder-plates ::ng-deep .nns-item-note-link{
      border: 1px solid #646A6A;
    }
  `],
  template: `
  <div class="nns-wrapper">
    <div class="nns-folder-title" [innerHTML]="folder.title"></div>
  </div>

  <nns-folder-empty
    *ngIf="children?.length == 0 && !share.isInvalidated"
    flex="noshrink" layout="row" layout-align="center center"
    [visual]="visual"
  ></nns-folder-empty>
  <div flex="none" *ngIf="children?.length != 0">
  <div class="nns-wrapper">
    <div *ngIf="visual.listType == 'list'"
      class="nns-folder-items nns-folder-folders-list nns-clearfix">
      <nns-item-folder-list
        *ngFor="let item of childFolders;trackBy:trackByGlobalId"
        [item]="item"
        [share]="share"
      ></nns-item-folder-list>
    </div>
    <div *ngIf="visual.listType == 'list'"
      class="nns-folder-items nns-folder-notes-list nns-clearfix">
      <nns-item-note-list
        *ngFor="let item of childNotes;trackBy:trackByGlobalId; let first = first;"
        [ngClass]="{'first': first}"
        [item]="item"
        [share]="share"
      ></nns-item-note-list>
    </div>
    <nns-loader
      *ngIf="!isMasonryReady && !visual.isMobile && isShowLoader && visual.listType == 'plates' && children?.length != 0"
      ></nns-loader>
    <div *ngIf="(visual.listType == 'plates') && !visual.isMobile"
      class="nns-folder-items nns-folder-plates nns-clearfix"
      masonry [options]="masonryOptions"
      [class.prepared]="!isMasonryReady"
      (layoutComplete)="layoutComplete()"
    >
      <nns-item-folder
        masonry-brick
        *ngFor="let item of childFolders;trackBy:trackByGlobalId"
        [item]="item"
        [share]="share"
      ></nns-item-folder>
      <nns-item-note
        masonry-brick
        *ngFor="let item of childNotes;trackBy:trackByGlobalId"
        [item]="item"
        [share]="share"
      ></nns-item-note>
    </div>

    <div *ngIf="(visual.listType == 'plates') && visual.isMobile"
      class="nns-folder-items nns-folder-folders-list nns-clearfix">
      <nns-item-folder-list
        *ngFor="let item of childFolders;trackBy:trackByGlobalId"
        [item]="item"
        [share]="share"
      ></nns-item-folder-list>
    </div>

    <div *ngIf="(visual.listType == 'plates') && visual.isMobile"
      class="nns-folder-items nns-folder-plates nns-clearfix"
      masonry [options]="masonryOptions"
    >
      <nns-item-note
        masonry-brick
        class="nns-title-2"
        *ngFor="let item of childNotes;trackBy:trackByGlobalId"
        [item]="item"
        [share]="share"
      ></nns-item-note>
    </div>
    <nns-show-more *ngIf="children.length > visual.folderChildrenCountToShow && (isMasonryReady || visual.isMobile)"></nns-show-more>
  </div>
  </div>
  `
})
export class FolderComponent implements OnChanges {
  @Input() public folder;
  @Input() public share;
  @Input() public children;
  @Input() public visual;
  @ViewChild(MasonryComponent) masonry: MasonryComponent;
  isMasonryReady = false;
  isShowLoader = false;

  childNotes;
  childFolders;
  masonryOptions = {
    itemSelector: 'nns-item-folder, nns-item-note',
    transitionDuration: 0
  };

  constructor() {
    setTimeout(() => {
      this.isShowLoader = true;
    }, 500)
  }

  layoutComplete() {
    this.isMasonryReady = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.children) {
      const children = changes.children.currentValue;
      this.showChildren(children)
    }

    if (changes.visual) {
      if (changes.visual.currentValue.listType === 'list') {
        this.isMasonryReady = false;
      }

      this.showChildren(this.children)
    }
  }

  showChildren(children) {
    const childNotes = children.filter((note) => note.type === 'note');
    const childFolders = children.filter((note) => note.type === 'folder');
    childFolders.sort((a, b) => {
      if (!a.title || !b.title) {
        return 0;
      }
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      if (aTitle < bTitle) {
        return -1;
      }
      if (aTitle > bTitle) {
        return 1;
      }
      return 0;
    });
    childNotes.sort((a, b) => {
      if (a.dateAdded < b.dateAdded) {
        return 1;
      }
      if (a.dateAdded > b.dateAdded) {
        return -1;
      }
      return 0;
    });
    this.childFolders = childFolders.filter(
      (item, index) => index < this.visual.folderChildrenCountToShow
    );

    this.childNotes = childNotes.filter(
      (item, index) => index < (this.visual.folderChildrenCountToShow - this.childFolders.length)
    );
  }

  trackByGlobalId(index, item) {
    return item.globalId;
  }
}
